import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Grid2 from "@mui/material/Unstable_Grid2";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";
import Tooltip from "@mui/material/Tooltip";

import { MuiSwitch } from "../../framework/muiSwitch";
import { InvoiceStateIndicator } from "./InvoiceStateIndicator";
import { TableDef, TableWrapper } from "../../framework/tableWrapper";
import { LoadingIndicator } from "../../framework/loadingIndicator";
import { InvoiceDetailsModal } from "./InvoiceDetailsModal";

import { RootState, useAppDispatch, useAppSelector } from "../../../framework/customStore";
import { InvoiceListItemDto } from "../../../models/transportOrderInvoice/invoiceListItemDto";
import { TransportOrderInvoiceState, transportOrderInvoiceStates } from "../../../models/transportOrderInvoice/transportOrderInvoice";
import { Base } from "../../../framework/base";
import { customHistory } from "../../../framework/customHistory";
import { showErrorMessage, showSuccessMessage } from "../../../models/store/storeActions";
import { showApiError, showConfirm } from "../../framework/formUtils";

import {
    approveInvoice,
    listIntegrations,
    removeInvoice,
    sendInvoiceToIntegration,
} from "../../../services/newInvoiceService";
import { formInvoiceQueryParams } from "./InvoiceListFilters";
import {
    fetchTransportOrderInvoice,
    setSelectedInvoice,
    setFilterDisplaySent,
    fetchTransportOrderInvoices,
    setInvoiceSendingToIntegration,
} from "../../../store/transportOrderInvoiceSlice";
import { useTranslation } from "react-i18next";
import MuiMenu from "../../framework/muiMenu";
import { IIdTitle } from "../../../models/common/idTitle";

export const TransportOrderInvoiceList = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const formatDate = (time: string) => Base.timeToDateTimeStr(new Date(time).getTime());
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [integrations, setIntegrations] = useState<IIdTitle[]>([]);
    const {
        selectedInvoice: invoiceDetails,
        filters
    } = useAppSelector((state: RootState) => state.transportOrderInvoice);

    const stateSentToIntegration = transportOrderInvoiceStates[TransportOrderInvoiceState.SentToIntegration];
    const allInvoices = useAppSelector((state: RootState) => state.transportOrderInvoice.transportOrderInvoices);
    const unsentInvoices = allInvoices.filter(invoice => invoice.state !== stateSentToIntegration?.value);

    useEffect(() => {
        listIntegrations()
            .then((res) => setIntegrations(res?.data ?? []))
            .catch(showApiError);
    }, []);

    const openModal = async(invoiceId: string) => {
        try {
            const resultAction = await (dispatch(fetchTransportOrderInvoice(invoiceId) as any));
            if (fetchTransportOrderInvoice.fulfilled.match(resultAction)) {
                if (invoiceDetails) setSelectedInvoice(invoiceDetails);
                setIsOpen(true);
            }
        } catch (error) {
            dispatch(showErrorMessage(t("common.fetchFailed")));
        }
    };

    const closeModal = () => {
        setSelectedInvoice(null);
        setIsOpen(false);
    };

    const handleToggleState = () => {
        dispatch(setFilterDisplaySent(!filters?.displaySent));
    };

    const handleApproveInvoice = (id: string, invoiceState: number) => {
        approveInvoice(id, invoiceState)
            .then(() => {
                dispatch(fetchTransportOrderInvoices(
                    formInvoiceQueryParams(filters)
                ) as any);
                dispatch(fetchTransportOrderInvoice(id) as any);
            })
            .catch((error) => {
                showApiError(error.message);
            });
    };

    const handleRemoveInvoice = (id: string) => {
        const submitRemove = () => {
            removeInvoice(id)
                .then(() => {
                    dispatch(showSuccessMessage(t("common.deleteWasSuccess")));
                    dispatch(fetchTransportOrderInvoices(
                        formInvoiceQueryParams(filters)
                    ) as any);
                    isOpen && closeModal();
                })
                .catch(showApiError);
        };
        showConfirm(t("common.areYouSureWantDelete"), submitRemove);
    };

    const handleRevokeApproval = (id: string) => {
        const submitRevokeApproval = () => {
            approveInvoice(id, TransportOrderInvoiceState.NotApproved)
                .then(() => {
                    dispatch(fetchTransportOrderInvoices(
                        formInvoiceQueryParams(filters)
                    ) as any);
                })
                .catch((error) => {
                    showApiError(error.message);
                });
        };
        showConfirm(t("common.areYouSureWantRevokeApproval"), submitRevokeApproval);
    };

    const sendToIntegration = (invoiceId: string, integrationId: string) => {
        dispatch(
            setInvoiceSendingToIntegration({
                id: invoiceId,
                sendingToIntegration: true
            })
        );

        sendInvoiceToIntegration(invoiceId, integrationId)
            .then(() => {
                dispatch(
                    showSuccessMessage(t("transport.invoicing.invoiceSent"))
                );
                dispatch(
                    fetchTransportOrderInvoices(
                        formInvoiceQueryParams(filters)
                    ) as any
                );
            })
            .catch((err) =>
                showApiError(err, t("transport.invoicing.invoiceSendFailed"))
            )
            .finally(() =>
                dispatch(
                    setInvoiceSendingToIntegration({
                        id: invoiceId,
                        sendingToIntegration: false,
                    })
                )
            );

    };

    const getSum = (sum: number | null) => {
        if (sum != null) {
            return sum.toFixed(2);
        }
        return "-";
    };

    const invoiceTable = useMemo<TableDef<InvoiceListItemDto>>(
        () => ({
            columns: [
                {
                    label: t("transport.invoicing.state"),
                    accessor: "state",
                    renderCell: (val: TransportOrderInvoiceState) => {
                        return <InvoiceStateIndicator state={val} />;
                    },
                },
                {
                    label: t("transport.invoicing.invoiceNumber"),
                    accessor: "invoiceNumber",
                },
                {
                    label: t("transport.invoicing.payersName"),
                    accessor: "customerName",
                },
                {
                    label: t("common.created"),
                    disabledSort: true,
                    accessor: (row) => formatDate(row.createdDateTime) || "",
                },
                {
                    label: t("common.sentDate"),
                    disabledSort: true,
                    renderCell: (_, row) =>
                        row.sentDateTime ? (
                            formatDate(row.sentDateTime)
                        ) : (
                            <Typography
                                style={{ fontStyle: "italic", color: "gray" }}
                            >
                                {t("common.notSent")}
                            </Typography>
                        ),
                },
                {
                    label: t("transport.invoicing.priceWithoutVAT") + " (EUR)",
                    disabledSort: true,
                    accessor: (row) => getSum(row?.totalVatExclusiveSum),
                },
                {
                    label: t("transport.invoicing.totalTax") + " (EUR)",
                    disabledSort: true,
                    accessor: (row) => getSum(row?.totalVatSum),
                },
                {
                    label:
                        t("transport.invoicing.totalInvoiceAmount") + " (EUR)",
                    disabledSort: true,
                    accessor: (row) => getSum(row?.totalVatInclusiveSum),
                },
                {
                    label: "",
                    renderCell: (_, row) =>
                        row.state === TransportOrderInvoiceState.Approved &&
                        integrations.length > 0 ? (
                            <MuiMenu
                                loading={row.sendingToIntegration}
                                iconButton={false}
                                buttonColor="success"
                                label={t("common.send")}
                                items={integrations.map((i) => ({
                                    label: i.title,
                                    onClick: () =>
                                        sendToIntegration(row.id, i.id),
                                }))}
                            />
                            ) : null,
                },
                {
                    label: "",
                    renderCell: (_, row) => (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                            }}
                        >
                            {row.state ===
                                TransportOrderInvoiceState.NotApproved && (
                                <>
                                    <IconButton
                                        color="primary"
                                        onClick={() =>
                                            customHistory.push(
                                                `/invoicingbeta/transportorderinvoice/${row.id}`,
                                                {
                                                    from: "/invoicingbeta/transportorderinvoices",
                                                }
                                            )
                                        }
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        color="error"
                                        onClick={() =>
                                            handleRemoveInvoice(row.id)
                                        }
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </>
                            )}
                            {row.state ===
                                TransportOrderInvoiceState.Approved && (
                                <Tooltip
                                    title={t("common.revokeApproval")}
                                    disableInteractive
                                >
                                    <IconButton
                                        color="primary"
                                        onClick={() => {
                                            handleRevokeApproval(row.id);
                                        }}
                                    >
                                        <LockIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <IconButton
                                color="primary"
                                onClick={() => void openModal(row.id)}
                            >
                                <VisibilityIcon />
                            </IconButton>
                        </div>
                    ),
                },
            ],
        }),
        [integrations]
    );

    if (!allInvoices && !unsentInvoices) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100vh"
            >
                <LoadingIndicator />
            </Box>
        );
    }

    return (
        <>
            <Grid2>
                <div style={{ padding: "1rem" }}>
                    <Grid2 container justifyContent="space-between">
                        <Grid2 xs={2}>
                            <Typography variant="h3">{t("transport.invoicing.transportOrderInvoices")}</Typography>
                        </Grid2>
                        <Grid2>
                            <MuiSwitch
                                key={stateSentToIntegration.value}
                                checked={filters?.displaySent}
                                onChange={() => handleToggleState()}
                                color={stateSentToIntegration.colorName}
                                label={t("transport.invoicing.displaySentInvoices")}
                            />
                        </Grid2>
                    </Grid2>
                    <Grid2>
                        <TableWrapper tableDef={invoiceTable} data={filters?.displaySent ? allInvoices : unsentInvoices} />
                    </Grid2>
                </div>
            </Grid2>
            {isOpen && invoiceDetails &&
                <InvoiceDetailsModal
                    id={invoiceDetails.id}
                    invoiceDetails={invoiceDetails}
                    open={isOpen}
                    handleClose={closeModal}
                    handleRemoveInvoice={handleRemoveInvoice}
                    handleApproveInvoice={handleApproveInvoice}
                    integrations={integrations}
                    handleSendToIntegration={sendToIntegration}
                />
            }
        </>
    );
};
